import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  useActiveMatches,
  useActivateMatch,
  useDeleteMatch,
  useUpsertMatch,
  useMatchEventsHistory,
  useMatchEventState,
} from '../../api/useMatchEvents';
import {
  useMatchEventsSocket,
  type MatchStateEntry,
} from '../../hooks/useMatchEventsSocket';
import {
  buildMatchEventRows,
  deriveSummary,
  derivePeriodMarkers,
  isBaseballSummary,
  isTennisSummary,
  mergeHistoryAndLive,
  pickFresherState,
  readMatchEventsFilterFromParams,
  secondsToClock,
  writeMatchEventsFilterToParams,
  VAR_STATUS_FINISHED,
  type MatchEventRow as MatchEventRowVM,
} from '../../utils/matchEventsFeed';
import {
  mergeMatchFeed,
  matchesOpFilter,
  formatOpConfidence,
  operationDisplay,
  operationTypeEmoji,
  operationTypeLabel,
  sourceBadge,
  formatOperationSummary,
} from '../../utils/matchOperationsFeed';
import { getCategoryDisplay } from './category-display';
import {
  SPORT_IDS,
  MATCH_EVENT_CATEGORIES,
  type MatchEventCategory,
  type MatchEventsFilter,
  type MatchOperation,
  type ActiveMatch,
  type OpFilterMode,
} from '../../types/matchEvents.types';
import type { MatchState } from '../../types/sniper.types';
import { useMatchEventSound } from '../../hooks/useMatchEventSound';
import { useVarReviewSound } from '../../hooks/useVarReviewSound';
import { useGoalAlerts } from '../../hooks/useGoalAlerts';
import { GoalAlertStack } from './GoalAlertStack';
import { SeedScoreControl } from './SeedScoreControl';
import { VarReviewBanner } from './VarReviewBanner';

/**
 * Whether the corner (secondary) sound cue should play. Muted only for a SOCCER
 * match with no corner markets — every other sport keeps its secondary cue
 * (baseball home runs, etc.), since the corner flag is soccer-only.
 */
export function cornerCueEnabled(
  isSoccer: boolean,
  hasCornerMarkets: boolean | undefined,
): boolean {
  return isSoccer ? hasCornerMarkets ?? false : true;
}

/**
 * Flatten the raw `Body` into label/value pairs for the expanded panel.
 * "All fields" per the contract — nothing is dropped: nulls render as `null`,
 * primitive arrays list their values, and objects deeper than the recursion
 * limit fall back to JSON instead of `[object Object]`.
 */
function flattenRaw(
  obj: Record<string, unknown>,
  prefix = '',
  depth = 0,
): Array<[string, string]> {
  const MAX_DEPTH = 3;
  const out: Array<[string, string]> = [];
  for (const [k, v] of Object.entries(obj)) {
    const label = prefix ? `${prefix} → ${k}` : k;
    if (v === null || v === undefined) {
      out.push([label, v === null ? 'null' : 'undefined']);
    } else if (Array.isArray(v)) {
      const isObjectArray =
        v.length > 0 && typeof v[0] === 'object' && v[0] !== null;
      if (v.length === 0) {
        out.push([label, '[]']);
      } else if (isObjectArray && depth < MAX_DEPTH) {
        v.forEach((item, idx) =>
          out.push(
            ...flattenRaw(
              item as Record<string, unknown>,
              `${label} [${idx + 1}]`,
              depth + 1,
            ),
          ),
        );
      } else {
        out.push([
          label,
          v
            .map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x)))
            .join(', '),
        ]);
      }
    } else if (typeof v === 'object' && depth < MAX_DEPTH) {
      out.push(...flattenRaw(v as Record<string, unknown>, label, depth + 1));
    } else if (typeof v === 'object') {
      out.push([label, JSON.stringify(v)]);
    } else {
      out.push([label, String(v)]);
    }
  }
  return out;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString();
  } catch {
    return iso;
  }
}

/** Confidence % → signal colour: 100 = confirmed (green), mid = amber, low = grey. */
function confidenceColor(pct: number): string {
  if (pct >= 100) return '#4caf50';
  if (pct >= 50) return '#ffb300';
  return '#9e9e9e';
}

/** Saturated per-side colours so the team stands out at a glance. */
const HOME_COLOR = '#5c93c4';
const AWAY_COLOR = '#c17a58';

/**
 * One match-event (incident) row — category label+emoji, a prominent
 * confidence chip up front, name, the team, value home:away, game clock,
 * period, receivedAt. Click the header toggles the expanded raw-`Body` panel.
 */
const MatchEventRow = memo(function MatchEventRow({
  row,
  homeTeam,
  awayTeam,
}: {
  row: MatchEventRowVM;
  homeTeam?: string;
  awayTeam?: string;
}) {
  const [open, setOpen] = useState(false);
  const { display, frame } = row;
  const homeLabel = homeTeam ?? 'Home';
  const awayLabel = awayTeam ?? 'Away';
  const isHomeAway = frame.homeValue != null && frame.awayValue != null;
  return (
    <Paper
      variant="outlined"
      data-testid="match-event-row"
      sx={{
        py: 0.5,
        pl: 1.5,
        pr: 1.5,
        mb: 0.5,
        borderLeft: 3,
        borderLeftColor: display.color,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        onClick={() => setOpen((o) => !o)}
        sx={{ cursor: 'pointer', minHeight: 28, flexWrap: 'wrap' }}
      >
        <Box
          aria-hidden
          sx={{ fontSize: '1rem', lineHeight: 1, minWidth: 22, textAlign: 'center' }}
        >
          {display.emoji}
        </Box>
        <Chip
          size="small"
          label={display.label}
          variant="outlined"
          sx={{
            borderColor: display.color,
            color: display.color,
            fontWeight: 500,
            height: 20,
            fontSize: '0.7rem',
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        {/* Tennis level (games / point / tie-break / set) so a games score is
            never confused with a sets or tie-break score. */}
        {row.levelLabel && (
          <Chip
            size="small"
            label={row.levelLabel}
            sx={{
              height: 18,
              fontSize: '0.65rem',
              fontWeight: 600,
              color: '#000',
              bgcolor: display.color,
              '& .MuiChip-label': { px: 0.6 },
            }}
          />
        )}
        <Box
          sx={{
            minWidth: 46,
            textAlign: 'center',
            px: 0.75,
            py: '2px',
            borderRadius: 1,
            fontWeight: 700,
            fontSize: '0.8rem',
            color: '#000',
            bgcolor: confidenceColor(row.confidencePct),
          }}
        >
          {row.confidencePct}%
        </Box>
        <Typography
          variant="body2"
          sx={{ color: 'common.white', fontWeight: 500 }}
        >
          {row.name}
        </Typography>
        {/* VAR open/close — a review emits two identical `VAR` rows (Started then
            Finished, separate incident keys), so without the status the operator
            cannot see in the log that the review already closed. An unreadable
            status renders "In review" (never nothing), matching the backend flag
            so the row can't contradict the banner above it. */}
        {row.statusLabel && (
          <Chip
            size="small"
            label={row.statusLabel}
            // "In review" is our fallback, not a wire value, and it renders amber
            // exactly like a real "Started" — without this an operator could not
            // tell a live review from LSports having stopped sending Body.Status.
            title={
              row.statusUnknown
                ? 'LSports sent no readable Body.Status for this VAR incident — treated as a review in progress'
                : `LSports Body.Status: ${row.statusLabel}`
            }
            sx={{
              height: 18,
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.03em',
              color: '#000',
              bgcolor:
                row.statusLabel === VAR_STATUS_FINISHED
                  ? 'success.main'
                  : 'warning.main',
              '& .MuiChip-label': { px: 0.6 },
            }}
          />
        )}
        {row.team && (
          <Chip
            size="small"
            label={row.team === 'home' ? homeLabel : awayLabel}
            sx={{
              height: 20,
              fontSize: '0.7rem',
              fontWeight: 700,
              color: '#fff',
              bgcolor: row.team === 'home' ? HOME_COLOR : AWAY_COLOR,
              '& .MuiChip-label': { px: 0.8 },
            }}
          />
        )}
        {row.playerName && (
          <Typography variant="caption" color="text.secondary">
            {row.playerName}
          </Typography>
        )}
        {isHomeAway ? (
          <Chip
            size="small"
            variant="outlined"
            label={
              <span>
                <span style={{ color: HOME_COLOR, fontWeight: 700 }}>
                  {homeLabel} {frame.homeValue}
                </span>
                {' – '}
                <span style={{ color: AWAY_COLOR, fontWeight: 700 }}>
                  {frame.awayValue} {awayLabel}
                </span>
              </span>
            }
            sx={{
              height: 20,
              fontSize: '0.7rem',
              '& .MuiChip-label': { px: 0.8 },
            }}
          />
        ) : (
          row.value != null && (
            <Chip
              size="small"
              label={row.value}
              variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.6 } }}
            />
          )
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {row.clock}
          {row.periodName ? ` · ${row.periodName}` : ''}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatTime(row.receivedAt)}
        </Typography>
      </Stack>
      {open && (
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
          <Stack spacing={0.5}>
            {flattenRaw(frame.raw).map(([label, value]) => (
              <Stack key={label} direction="row" spacing={2} alignItems="flex-start">
                <Typography
                  variant="caption"
                  sx={{ minWidth: 200, color: 'text.secondary', flexShrink: 0 }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'common.white', wordBreak: 'break-word', flex: 1 }}
                >
                  {value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
});

/**
 * One `match_operation` row — the sniper/manual action lane (mirrors the
 * opener's auto/manual notification styling): a lane emoji (⚡ automatic /
 * ✋ manual), the op-type chip, a phase-aware summary phrase, and the timestamp.
 */
const OperationRow = memo(function OperationRow({ op }: { op: MatchOperation }) {
  const display = operationDisplay(op);
  const typeLabel = operationTypeLabel(op.type);
  const glyph = operationTypeEmoji(op);
  const confidence = formatOpConfidence(op);
  const summary = formatOperationSummary(op);
  return (
    <Paper
      variant="outlined"
      sx={{
        py: 0.5,
        pl: 1.5,
        pr: 1.5,
        mb: 0.5,
        borderLeft: 3,
        borderLeftColor: display.color,
        bgcolor: 'action.hover',
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ minHeight: 28, flexWrap: 'wrap' }}
      >
        <Box
          aria-hidden
          sx={{ fontSize: '1rem', lineHeight: 1, minWidth: 22, textAlign: 'center' }}
        >
          {glyph}
        </Box>
        <Chip
          size="small"
          label={display.label}
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 700,
            color: '#fff',
            bgcolor: display.color,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        <Chip
          size="small"
          variant="outlined"
          title={op.type}
          label={typeLabel}
          sx={{
            borderColor: display.color,
            color: display.color,
            fontWeight: 500,
            height: 20,
            fontSize: '0.7rem',
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        {/* How sure LSports was about the frame this op is about — on a retraction
            that number IS the decision, so it sits beside the type, not buried in
            the summary tail. */}
        {confidence && (
          <Chip
            size="small"
            label={confidence}
            title="LSports confidence for the incident this op is about"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              fontWeight: 700,
              color: '#fff',
              bgcolor: '#616161',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
        {summary && (
          <Typography variant="body2" sx={{ color: 'common.white' }}>
            {summary}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatTime(op.ts)}
        </Typography>
      </Stack>
    </Paper>
  );
});

/**
 * Compact per-sport scoreboard line for a match header. Soccer score/corners/VAR
 * come from the BACKEND-authored `MatchState` (never FE-derived); baseball +
 * tennis still derive from the raw feed (no backend state contract for them yet).
 */
function ScoreLine({
  match,
  frames,
  sport,
  state,
}: {
  match: ActiveMatch;
  frames: Parameters<typeof deriveSummary>[0];
  sport: number | null | undefined;
  state: MatchState | null;
}) {
  const summary = useMemo(() => deriveSummary(frames, sport), [frames, sport]);

  if (isBaseballSummary(summary)) {
    const bb = summary;
    if (!(bb.runs || bb.inning || bb.final)) return null;
    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {bb.runs && (
          <Typography variant="body2" sx={{ color: 'common.white' }}>
            ⚾ {match.homeTeam}{' '}
            <b>
              {bb.runs.home}–{bb.runs.away}
            </b>{' '}
            {match.awayTeam}
          </Typography>
        )}
        {bb.inning && (
          <Chip size="small" variant="outlined" label={`⚾ ${bb.inning}`} sx={{ height: 20 }} />
        )}
        {bb.final && (
          <Chip
            size="small"
            label="🏁 Final"
            sx={{ height: 20, bgcolor: '#9c27b0', color: '#fff', fontWeight: 700 }}
          />
        )}
      </Stack>
    );
  }
  if (isTennisSummary(summary)) {
    const tn = summary;
    if (!(tn.game || tn.set || tn.final)) return null;
    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {tn.game && (
          <Typography variant="body2" sx={{ color: 'common.white' }}>
            🎾 {match.homeTeam}{' '}
            <b>
              {tn.game.home}–{tn.game.away}
            </b>{' '}
            {match.awayTeam}
          </Typography>
        )}
        {tn.set && (
          <Chip size="small" variant="outlined" label={`🏅 ${tn.set}`} sx={{ height: 20 }} />
        )}
        {tn.final && (
          <Chip
            size="small"
            label="🏁 Final"
            sx={{ height: 20, bgcolor: '#9c27b0', color: '#fff', fontWeight: 700 }}
          />
        )}
      </Stack>
    );
  }

  // Soccer (goals + corners) — rendered from the BACKEND `match_state`, never
  // FE-derived. No state yet = the baseline is not established (feed cold-started
  // mid-match) → show an explicit "score syncing…" placeholder + the operator
  // seed control, NEVER a fabricated 0–0.
  if (!state) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" color="text.secondary">
          ⚽ {match.homeTeam} vs {match.awayTeam} · score syncing…
        </Typography>
        <SeedScoreControl
          fixtureId={match.fixtureId}
          homeTeam={match.homeTeam}
          awayTeam={match.awayTeam}
          mode="seed"
          showCorners={match.hasCornerMarkets ?? false}
        />
      </Stack>
    );
  }
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <Typography variant="body2" sx={{ color: 'common.white' }}>
        ⚽ {match.homeTeam}{' '}
        <b>
          {state.homeScore}–{state.awayScore}
        </b>{' '}
        {match.awayTeam}
      </Typography>
      <Chip
        size="small"
        variant="outlined"
        label={`🚩 ${state.homeCorners}–${state.awayCorners}`}
        sx={{ height: 20, fontSize: '0.7rem' }}
      />
      {/* Backend-authored live clock (mm'ss). No clock yet → render nothing,
          never a fabricated time. */}
      {state.clock != null && (
        <Chip
          size="small"
          variant="outlined"
          label={`⏱️ ${secondsToClock(state.clock)}`}
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
      )}
      <VarReviewBanner active={state.lastVarActive} dense />
      {/* Override: the feed can mis-state a score — let the operator correct the
          established baseline (collapsed behind a "Correct score" button). */}
      <SeedScoreControl
        fixtureId={match.fixtureId}
        homeTeam={match.homeTeam}
        awayTeam={match.awayTeam}
        mode="override"
        showCorners={match.hasCornerMarkets ?? false}
      />
    </Stack>
  );
}

/**
 * One active match in the hub — a collapsible card. Header: teams, `source`
 * badge (⚡ group / ✋ manual / both), `live` chip, compact scoreboard, delete.
 * Body: ONE timestamp-ordered feed merging this fixture's incidents
 * (`match_event`, category/confidence filtered) with its operations
 * (`match_operation`, auto/manual filtered) plus period markers.
 */
const MatchSection = memo(function MatchSection({
  match,
  liveFrames,
  liveState,
  operations,
  filter,
  soundsEnabled,
  onDelete,
  deleting,
}: {
  match: ActiveMatch;
  liveFrames: Parameters<typeof deriveSummary>[0];
  /** Live backend `match_state` for this fixture (WS), if one has arrived. */
  liveState: MatchStateEntry | undefined;
  operations: MatchOperation[];
  filter: MatchEventsFilter;
  soundsEnabled: boolean;
  onDelete: (fixtureId: number) => void;
  deleting: boolean;
}) {
  const [expanded, setExpanded] = useState(true);

  // Durable history (24h TTL) merged with the live buffer so a refresh rebuilds
  // the full feed instead of only the ~20-min live replay window.
  const { data: history = [], isFetched: historyFetched } = useMatchEventsHistory(
    match.fixtureId,
  );
  const frames = useMemo(
    () => mergeHistoryAndLive(history, liveFrames),
    [history, liveFrames],
  );

  // Sport: the LIVE feed's resolved sport wins; the stored pick only seeds the
  // view before any frame arrives.
  const frameSportId = useMemo(() => {
    for (const f of frames) if (f.sportId != null) return f.sportId;
    return null;
  }, [frames]);
  const sport = frameSportId ?? match.sportId;

  // Backend-authored MatchState is a SOCCER-only contract; baseball + tennis
  // derive their scoreboard from the raw feed, so skip the wasted null request
  // for them (unknown/unresolved sport is treated as a soccer candidate).
  const isSoccerState =
    sport == null || sport === SPORT_IDS.SOCCER;

  // REST backfill for the first render, superseded by the live `match_state` WS
  // push — but the FRESHER `ts` wins, so a stale live value can't shadow a
  // reconnect-recovered backfill. Soccer score/corners/clock/VAR render from this.
  const { data: stateBackfill } = useMatchEventState(
    isSoccerState ? match.fixtureId : null,
  );
  const matchState = pickFresherState(liveState, stateBackfill);

  const summary = useMemo(() => deriveSummary(frames, sport), [frames, sport]);
  useMatchEventSound(summary, {
    enabled: soundsEnabled,
    fixtureId: match.fixtureId,
    ready: historyFetched,
    secondaryEnabled: cornerCueEnabled(isSoccerState, match.hasCornerMarkets),
    goalFrames: sport === SPORT_IDS.SOCCER ? frames : undefined,
  });
  useVarReviewSound(matchState?.lastVarActive, {
    enabled: soundsEnabled,
    fixtureId: match.fixtureId,
  });

  const rows = useMemo(() => buildMatchEventRows(frames, filter), [frames, filter]);
  const markers = useMemo(() => derivePeriodMarkers(frames), [frames]);
  const filteredOps = useMemo(
    () => operations.filter((op) => matchesOpFilter(op, filter.opMode)),
    [operations, filter.opMode],
  );
  const feed = useMemo(
    () => mergeMatchFeed(rows, filteredOps, markers),
    [rows, filteredOps, markers],
  );

  const badge = sourceBadge(match.source);
  const opsCount = filteredOps.length;

  return (
    <Paper variant="outlined" sx={{ mb: 1.5, overflow: 'hidden' }}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ p: 1.5, flexWrap: 'wrap', bgcolor: 'background.default' }}
        useFlexGap
      >
        <IconButton size="small" onClick={() => setExpanded((e) => !e)} aria-label="toggle match">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
        <Typography variant="subtitle1" sx={{ color: 'common.white', fontWeight: 600 }}>
          {match.homeTeam} vs {match.awayTeam}
        </Typography>
        {match.league && (
          <Typography variant="caption" color="text.secondary">
            {match.league}
          </Typography>
        )}
        <Tooltip title={`Source: ${badge.label}`}>
          <Chip
            size="small"
            label={`${badge.emoji} ${badge.label}`}
            sx={{
              height: 22,
              fontWeight: 700,
              color: '#fff',
              bgcolor: badge.color,
              '& .MuiChip-label': { px: 0.9 },
            }}
          />
        </Tooltip>
        <Chip
          size="small"
          color={match.live ? 'success' : 'default'}
          label={match.live ? 'live' : match.active ? 'activating…' : 'inactive'}
          sx={{ height: 22 }}
        />
        {opsCount > 0 && (
          <Chip
            size="small"
            variant="outlined"
            label={`${opsCount} ops`}
            sx={{ height: 22 }}
          />
        )}
        <Box sx={{ ml: { md: 2 } }}>
          <ScoreLine match={match} frames={frames} sport={sport} state={matchState} />
        </Box>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Delete match (deactivate + remove)">
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={deleting}
              onClick={() => onDelete(match.fixtureId)}
              aria-label="delete match"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ p: 1.5, pt: 1 }}>
          {feed.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              Waiting for live events…
            </Typography>
          ) : (
            feed.map((item) =>
              item.kind === 'marker' ? (
                <Divider
                  key={item.key}
                  textAlign="center"
                  sx={{ my: 1, '&::before, &::after': { borderColor: 'warning.main' } }}
                >
                  <Chip
                    size="small"
                    label={`⏱️ ${item.label}`}
                    sx={{ bgcolor: 'warning.main', color: '#000', fontWeight: 700 }}
                  />
                </Divider>
              ) : item.kind === 'operation' ? (
                <OperationRow key={item.key} op={item.op} />
              ) : (
                <MatchEventRow
                  key={item.key}
                  row={item.row}
                  homeTeam={match.homeTeam}
                  awayTeam={match.awayTeam}
                />
              ),
            )
          )}
        </Box>
      </Collapse>
    </Paper>
  );
});

/**
 * The Live Match Events hub: a multi-match operations view. Lists ALL active
 * matches (`GET /match-events/active`), subscribes to EVERY active fixture's
 * room, and renders each as a collapsible card whose single feed merges live
 * incidents (`match_event`) with sniper/manual operations (`match_operation`).
 * Global filters (categories / confidence / Auto|Manual|All) apply across
 * matches; a match multi-select scopes which cards show. Filters persist to
 * URL `ev*` params (`tab` is preserved).
 */
export function LiveMatchEventsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<MatchEventsFilter>(() =>
    readMatchEventsFilterFromParams(searchParams),
  );

  useEffect(() => {
    setSearchParams(
      (prev) => writeMatchEventsFilterToParams(filter, new URLSearchParams(prev)),
      { replace: true },
    );
  }, [filter, setSearchParams]);

  const { data: matches = [], isLoading, isError } = useActiveMatches();
  const activate = useActivateMatch();
  const upsert = useUpsertMatch();
  const del = useDeleteMatch();

  // Subscribe to EVERY active fixture (independent of the visibility filter),
  // so incidents + operations for all matches are always buffered.
  const activeFixtureIds = useMemo(
    () => matches.map((m) => m.fixtureId),
    [matches],
  );
  const { isConnected, frames, operations, matchState } = useMatchEventsSocket({
    fixtureIds: activeFixtureIds,
    includeRaw: filter.raw,
  });

  // Group the flat buffers by fixture once, so each section slices cheaply.
  const framesByFixture = useMemo(() => {
    const map = new Map<number, typeof frames>();
    for (const f of frames) {
      const arr = map.get(f.fixtureId);
      if (arr) arr.push(f);
      else map.set(f.fixtureId, [f]);
    }
    return map;
  }, [frames]);
  const opsByFixture = useMemo(() => {
    const map = new Map<number, MatchOperation[]>();
    for (const op of operations) {
      const arr = map.get(op.fixtureId);
      if (arr) arr.push(op);
      else map.set(op.fixtureId, [op]);
    }
    return map;
  }, [operations]);

  // Match visibility multi-select — empty ⇒ show all.
  const [visibleIds, setVisibleIds] = useState<number[]>([]);
  const visibleMatches = useMemo(() => {
    if (visibleIds.length === 0) return matches;
    const set = new Set(visibleIds);
    return matches.filter((m) => set.has(m.fixtureId));
  }, [matches, visibleIds]);

  // Cross-match GOAL detector: raises a prominent notification when any active
  // fixture's score increases, and floats the scoring match to the top.
  const { alerts: goalAlerts, dismiss: dismissGoal, lastGoalAt } = useGoalAlerts(
    matchState,
    matches,
  );
  const orderedMatches = useMemo(() => {
    // Most-recently-scored match first; the rest keep their original order
    // (Array.sort is stable, so a 0 comparison preserves it).
    return [...visibleMatches].sort(
      (x, y) =>
        (lastGoalAt.get(y.fixtureId) ?? 0) - (lastGoalAt.get(x.fixtureId) ?? 0),
    );
  }, [visibleMatches, lastGoalAt]);

  // Add-match form: register (upsert) an LSports fixture, then activate it so
  // it joins the active hub list.
  const [newMatch, setNewMatch] = useState<{
    fixtureId: string;
    homeTeam: string;
    awayTeam: string;
    league: string;
    sport: 'auto' | 'soccer' | 'baseball' | 'tennis';
  }>({ fixtureId: '', homeTeam: '', awayTeam: '', league: '', sport: 'auto' });
  const newFixtureIdNum = Number(newMatch.fixtureId);
  const canAddMatch =
    Number.isInteger(newFixtureIdNum) &&
    newFixtureIdNum > 0 &&
    newMatch.homeTeam.trim().length > 0 &&
    newMatch.awayTeam.trim().length > 0;

  const addMatch = useCallback(() => {
    if (!canAddMatch) return;
    const sportId =
      newMatch.sport === 'soccer'
        ? SPORT_IDS.SOCCER
        : newMatch.sport === 'baseball'
          ? SPORT_IDS.BASEBALL
          : newMatch.sport === 'tennis'
            ? SPORT_IDS.TENNIS
            : undefined;
    upsert.mutate(
      {
        fixtureId: newFixtureIdNum,
        homeTeam: newMatch.homeTeam.trim(),
        awayTeam: newMatch.awayTeam.trim(),
        league: newMatch.league.trim() || null,
        ...(sportId != null ? { sportId } : {}),
      },
      {
        onSuccess: () => {
          // Activate so the freshly-registered match joins the active hub list.
          activate.mutate(newFixtureIdNum);
          setNewMatch({
            fixtureId: '',
            homeTeam: '',
            awayTeam: '',
            league: '',
            sport: 'auto',
          });
        },
      },
    );
  }, [canAddMatch, newFixtureIdNum, newMatch, upsert, activate]);

  const [soundsEnabled, setSoundsEnabled] = useState(true);

  const setCategories = useCallback((categories: MatchEventCategory[]) => {
    setFilter((f) => ({ ...f, categories }));
  }, []);

  const onDelete = useCallback(
    (fixtureId: number) => {
      del.mutate(fixtureId);
    },
    [del],
  );

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexShrink: 0 }}>
        <Typography variant="h5" sx={{ color: 'common.white' }}>
          Live match events
        </Typography>
        <Chip
          size="small"
          color={isConnected ? 'success' : 'default'}
          label={isConnected ? 'live' : 'disconnected'}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`${matches.length} active`}
        />
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={soundsEnabled}
              onChange={(e) => setSoundsEnabled(e.target.checked)}
              inputProps={{ 'aria-label': 'goal and corner sounds' }}
            />
          }
          label="🔊 Sounds"
        />
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, flexShrink: 0 }}>
        <Stack spacing={2}>
          {/* Add a match by LSports fixture ID → activate → joins the hub. */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ md: 'center' }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96 }}>
              Add match
            </Typography>
            <TextField
              size="small"
              label="Fixture ID"
              value={newMatch.fixtureId}
              onChange={(e) =>
                setNewMatch((m) => ({
                  ...m,
                  fixtureId: e.target.value.replace(/[^0-9]/g, ''),
                }))
              }
              inputProps={{ inputMode: 'numeric' }}
              sx={{ width: 130 }}
            />
            <TextField
              size="small"
              label="Home team"
              value={newMatch.homeTeam}
              onChange={(e) => setNewMatch((m) => ({ ...m, homeTeam: e.target.value }))}
              sx={{ flex: 1, minWidth: 150 }}
            />
            <TextField
              size="small"
              label="Away team"
              value={newMatch.awayTeam}
              onChange={(e) => setNewMatch((m) => ({ ...m, awayTeam: e.target.value }))}
              sx={{ flex: 1, minWidth: 150 }}
            />
            <TextField
              size="small"
              label="League (optional)"
              value={newMatch.league}
              onChange={(e) => setNewMatch((m) => ({ ...m, league: e.target.value }))}
              sx={{ flex: 1, minWidth: 140 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="add-match-sport">Sport</InputLabel>
              <Select
                labelId="add-match-sport"
                label="Sport"
                value={newMatch.sport}
                onChange={(e) =>
                  setNewMatch((m) => ({
                    ...m,
                    sport: e.target.value as 'auto' | 'soccer' | 'baseball' | 'tennis',
                  }))
                }
              >
                <MenuItem value="auto">Auto</MenuItem>
                <MenuItem value="soccer">⚽ Soccer</MenuItem>
                <MenuItem value="baseball">⚾ Baseball</MenuItem>
                <MenuItem value="tennis">🎾 Tennis</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              onClick={addMatch}
              disabled={!canAddMatch || upsert.isLoading}
            >
              {upsert.isLoading ? 'Adding…' : 'Add'}
            </Button>
          </Stack>

          <Divider flexItem />

          {/* Filter bar: which matches, which categories, confidence, ops. */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
          >
            <FormControl size="small" sx={{ flex: 1, minWidth: 220 }}>
              <InputLabel id="match-filter">Matches</InputLabel>
              <Select
                labelId="match-filter"
                multiple
                value={visibleIds.map(String)}
                onChange={(e) => {
                  const v = e.target.value;
                  const arr = typeof v === 'string' ? v.split(',') : v;
                  setVisibleIds(arr.filter(Boolean).map(Number));
                }}
                input={<OutlinedInput label="Matches" />}
                displayEmpty
                renderValue={(selected) =>
                  (selected as string[]).length === 0 ? (
                    <em>All matches</em>
                  ) : (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(selected as string[]).map((id) => {
                        const m = matches.find((x) => String(x.fixtureId) === id);
                        return (
                          <Chip
                            key={id}
                            size="small"
                            label={m ? `${m.homeTeam} v ${m.awayTeam}` : id}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        );
                      })}
                    </Stack>
                  )
                }
              >
                {matches.map((m) => (
                  <MenuItem key={m.fixtureId} value={String(m.fixtureId)}>
                    {m.homeTeam} vs {m.awayTeam}
                    {m.league ? ` · ${m.league}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ flex: 1, minWidth: 220 }}>
              <InputLabel id="category-filter">Categories</InputLabel>
              <Select
                labelId="category-filter"
                multiple
                value={filter.categories}
                onChange={(e) =>
                  setCategories(
                    (typeof e.target.value === 'string'
                      ? e.target.value.split(',')
                      : e.target.value) as MatchEventCategory[],
                  )
                }
                input={<OutlinedInput label="Categories" />}
                renderValue={(selected) => (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {(selected as MatchEventCategory[]).map((c) => {
                      const d = getCategoryDisplay(c);
                      return (
                        <Chip
                          key={c}
                          size="small"
                          label={`${d.emoji} ${d.label}`}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      );
                    })}
                  </Stack>
                )}
              >
                {MATCH_EVENT_CATEGORIES.map((c) => {
                  const d = getCategoryDisplay(c);
                  return (
                    <MenuItem key={c} value={c}>
                      {d.emoji} {d.label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <Box sx={{ flex: 1, minWidth: 160, px: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Min confidence: {Math.round(filter.minConfidence * 100)}%
              </Typography>
              <Slider
                size="small"
                min={0}
                max={1}
                step={0.05}
                value={filter.minConfidence}
                onChange={(_, v) =>
                  setFilter((f) => ({
                    ...f,
                    minConfidence: Array.isArray(v) ? v[0] : v,
                  }))
                }
                aria-label="minimum confidence"
              />
            </Box>
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Operations
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={filter.opMode}
                onChange={(_, v: OpFilterMode | null) => {
                  if (v) setFilter((f) => ({ ...f, opMode: v }));
                }}
                aria-label="operations filter"
              >
                <ToggleButton value="auto" aria-label="automatic operations">
                  ⚡ Auto
                </ToggleButton>
                <ToggleButton value="manual" aria-label="manual operations">
                  ✋ Manual
                </ToggleButton>
                <ToggleButton value="all" aria-label="all operations">
                  All
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box sx={{ flex: 1 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={filter.showAll}
                  onChange={(e) => setFilter((f) => ({ ...f, showAll: e.target.checked }))}
                  inputProps={{ 'aria-label': 'show all updates' }}
                />
              }
              label="Show all updates"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={filter.raw}
                  onChange={(e) => setFilter((f) => ({ ...f, raw: e.target.checked }))}
                  inputProps={{ 'aria-label': 'raw all incidents' }}
                />
              }
              label="Raw / all incidents"
            />
          </Stack>
        </Stack>
      </Paper>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {isError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            Failed to load active matches.
          </Alert>
        )}
        {isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Stack>
        )}
        {!isLoading && matches.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No active matches — add one above (or activate one from a sniper group).
          </Typography>
        )}
        {orderedMatches.map((m) => (
          <MatchSection
            key={m.fixtureId}
            match={m}
            liveFrames={framesByFixture.get(m.fixtureId) ?? []}
            liveState={matchState.get(m.fixtureId)}
            operations={opsByFixture.get(m.fixtureId) ?? []}
            filter={filter}
            soundsEnabled={soundsEnabled}
            onDelete={onDelete}
            deleting={del.isLoading}
          />
        ))}
      </Box>
      <GoalAlertStack alerts={goalAlerts} onDismiss={dismissGoal} />
    </Box>
  );
}
